import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  ApplicationInfoLevel,
  ApplicationStatus,
  ClientType,
  FlowTypes,
} from '~/enums';
import {
  DataPointsShared,
  ILoanApplicationApplicant,
  ILoanApplicationForm,
  ILoanApplicationOverview,
  ILoanApplicationState,
} from '~/interfaces/loanApplication';
import { listenerMiddleware } from '../middlewares/listener.middleware';
import { ApiStatus } from '../types/sharedTypes';

export const loanApplicationInitialState = Object.freeze<ILoanApplicationState>(
  {
    currentStepNumber: 0,
    currentStepName: null,
    nextStepName: null,
    stepStartTimestamp: new Date().toString(),
    // Flowtype is manual unless user authenticates in launchview
    flowType: FlowTypes.Manual,
    isCompanyTypeCategoryRequested: false,
    form: {
      applicationUuid: '',
      amount: '',
      clientType: ClientType.Unknown,
      guarantors: [],
      purposeOfLoan: '',
      organizationNumber: '',
      companyType: '',
      companyName: '',
      reason: '',
      sessionId: '',
      applicantIdentified: false,
      applicantAuthenticated: false,
      turnover: '',
      applicant: {},
      incomeReceivedInForeignCurrencies: undefined,
      companyTypeCategory: undefined,
      bankConnected: false,
      choseToBoostApplication: false,
      choseToSubmitBasicApplication: false,
      cardOptIn: undefined,
    },
    overview: {
      clients: [],
    },
    apiStatus: {
      guarantors: ApiStatus.Idle,
      clients: ApiStatus.Idle,
      post: ApiStatus.Idle,
      patch: ApiStatus.Idle,
      companyLookupData: ApiStatus.Idle,
      application: ApiStatus.Idle,
      companySearch: ApiStatus.Idle,
    },
    applicationStatus: ApplicationStatus.None,
    companyIsNew: false,
    infoLevel: ApplicationInfoLevel.PRE_BASIC,
    dataPointsShared: {
      turnover: false,
      loanPurpose: false,
      cardOptIn: false,
    },
  }
);

const loanApplicationSlice = createSlice({
  name: 'loanApplication',
  initialState: loanApplicationInitialState,
  reducers: {
    updateCurrentStepNumber: (state, action: PayloadAction<number>) => {
      state.currentStepNumber = action.payload;
    },
    goToNextStep: (state) => {
      state.currentStepNumber += 1;
    },
    goToPreviousStep: (state) => {
      state.currentStepNumber -= 1;
    },
    setCurrentStepName: (
      state,
      action: PayloadAction<ILoanApplicationState['currentStepName']>
    ) => {
      state.currentStepName = action.payload;
    },
    setNextStepName: (
      state,
      action: PayloadAction<ILoanApplicationState['nextStepName']>
    ) => {
      state.nextStepName = action.payload;
    },
    updateFlowType: (state, action: PayloadAction<FlowTypes>) => {
      state.flowType = action.payload;
    },
    setCompanyIsNew: (state, action: PayloadAction<boolean>) => {
      state.companyIsNew = action.payload;
    },
    updateForm: (
      state,
      action: PayloadAction<Partial<ILoanApplicationForm>>
    ) => {
      state.form = { ...state.form, ...action.payload };
    },
    resetCompanyInForm: (state) => {
      state.form.companyName = '';
      state.form.companyAddress = undefined;
      state.form.organizationNumber = undefined;
      state.form.vatNumber = undefined;
      state.form.companyTypeCategory = undefined;
      state.form.connectId = undefined;
      state.form.creditReportProvider = undefined;
      state.form.clientType = ClientType.NLC;
    },
    setUserId: (state, action: PayloadAction<number>) => {
      state.userId = action.payload;
    },
    setClientId: (state, action: PayloadAction<number>) => {
      state.clientId = action.payload;
    },
    updateOverview: (
      state,
      action: PayloadAction<ILoanApplicationOverview>
    ) => {
      state.overview = { ...state.overview, ...action.payload };
    },
    setApiStatus: (
      state,
      action: PayloadAction<Partial<ILoanApplicationState['apiStatus']>>
    ) => {
      state.apiStatus = {
        ...state.apiStatus,
        ...action.payload,
      };
    },
    setStepStartTimestamp: (state) => {
      state.stepStartTimestamp = new Date().toString();
    },
    updateApplicationStatus: (
      state,
      action: PayloadAction<ApplicationStatus>
    ) => {
      state.applicationStatus = action.payload;
    },
    updateCurrentOrganizationIdentifier: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.currentApplicationOrganizationIdentifier = action.payload ?? '';
    },
    setIsCompanyTypeCategoryRequested: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isCompanyTypeCategoryRequested = action.payload;
    },
    clearLoanApplicationState: () => ({
      ...loanApplicationInitialState,
      cleared: true,
    }),
    removeClearedState: (state) => {
      state.cleared = false;
    },
    updateApplicantData: (
      state,
      action: PayloadAction<ILoanApplicationApplicant>
    ) => {
      state.form.applicant = {
        ...state.form.applicant,
        ...action.payload,
      };
    },
    setBankConnectionError: (state, action: PayloadAction<boolean>) => {
      state.bankConnectionError = action.payload;
    },
    setInfoLevel: (state, action: PayloadAction<ApplicationInfoLevel>) => {
      state.infoLevel = action.payload;
    },
    setDataPointsShared: (
      state,
      action: PayloadAction<Partial<DataPointsShared>>
    ) => {
      state.dataPointsShared = {
        ...state.dataPointsShared,
        ...action.payload,
      };
    },
  },
});

export const {
  updateCurrentStepNumber,
  goToNextStep,
  goToPreviousStep,
  setCurrentStepName,
  setNextStepName,
  updateFlowType,
  setCompanyIsNew,
  updateForm,
  resetCompanyInForm,
  setUserId,
  setClientId,
  updateOverview,
  setApiStatus,
  updateApplicationStatus,
  updateCurrentOrganizationIdentifier,
  setIsCompanyTypeCategoryRequested,
  clearLoanApplicationState,
  removeClearedState,
  updateApplicantData,
  setBankConnectionError,
  setInfoLevel,
  setDataPointsShared,
} = loanApplicationSlice.actions;

export default loanApplicationSlice.reducer;

listenerMiddleware.startListening({
  matcher: isAnyOf(updateCurrentStepNumber, goToNextStep, goToPreviousStep),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(loanApplicationSlice.actions.setStepStartTimestamp());
  },
});
